import { JSONContent } from '@tiptap/core';
import { Color } from '@tiptap/extension-color';
import { HardBreak } from '@tiptap/extension-hard-break';
import Highlight from '@tiptap/extension-highlight';
import Placeholder from '@tiptap/extension-placeholder';
import TaskItem from '@tiptap/extension-task-item';
import TaskList from '@tiptap/extension-task-list';
import { TextStyle } from '@tiptap/extension-text-style';
import StarterKit from '@tiptap/starter-kit';

// @tiptap/markdown customizes a node's markdown serialization by reading a
// `renderMarkdown` field off its extension config (via getExtensionField). That
// field isn't part of @tiptap/core's typed config, and NodeConfig is the only
// place it can live (it's what getExtensionField resolves), so we augment it.
// The library invokes it with three args — (node, helpers, context) — but ships
// no types for helpers/context, and our only consumer (HardBreak below) ignores
// them. Keep them loosely typed; add precise helper/context types if a future
// serializer (e.g. underline/highlight) actually needs them.
declare module '@tiptap/core' {
  // Generic params mirror @tiptap/core's NodeConfig so declaration merging works;
  // they're unused in this augmentation body.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars
  interface NodeConfig<Options = any, Storage = any> {
    renderMarkdown?: (
      node: JSONContent,
      helpers?: unknown,
      context?: unknown,
    ) => string;
  }
}

// We don't want to show the slash command placeholder for these node types because it creates
// a weird visual bug where the placeholder is shown stacked multiple times
const nodeTypesToIgnoreForSlashCommandPlaceholder = [
  'bulletList',
  'orderedList',
  'taskList',
  'taskItem',
];

export const extensions = [
  // v3 StarterKit bundles Link, Underline, HorizontalRule, the list extensions
  // and ListKeymap, so they're configured here instead of as separate extensions.
  StarterKit.configure({
    bulletList: {
      HTMLAttributes: {
        class: 'list-disc list-outside leading-3 -mt-2',
      },
    },
    orderedList: {
      HTMLAttributes: {
        class: 'list-decimal list-outside leading-3 -mt-2',
      },
    },
    listItem: {
      HTMLAttributes: {
        class: 'leading-normal -mb-2',
      },
    },
    blockquote: {
      HTMLAttributes: {
        class: 'border-l-4 border-stone-700',
      },
    },
    code: {
      HTMLAttributes: {
        class:
          'rounded-md bg-stone-200 px-1.5 py-1 font-mono font-medium text-stone-900',
        spellcheck: 'false',
      },
    },
    // The v2 horizontal-rule input-rule patch (PR #3859) is upstreamed in v3
    // (PR #4289), so we use StarterKit's HorizontalRule directly.
    horizontalRule: {
      HTMLAttributes: {
        class: 'mt-4 mb-6 border-t border-stone-300',
      },
    },
    link: {
      HTMLAttributes: {
        class:
          'text-stone-400 underline underline-offset-[3px] hover:text-stone-600 transition-colors cursor-pointer',
      },
    },
    dropcursor: {
      color: '#DBEAFE',
      width: 4,
    },
    gapcursor: false,
    // Disable StarterKit's HardBreak so we can supply one with a custom markdown
    // serializer (below) — its default emits an escaped hard break (`\` + newline).
    hardBreak: false,
  }),
  // Serialize hard breaks as a plain newline so the model's single-newline regions
  // (e.g. the ENT Objective lines Telinga / Hidung / Tenggorok) round-trip
  // byte-identically instead of gaining a `\` on every line. See ENG-4453.
  HardBreak.extend({
    renderMarkdown: () => '\n',
  }),
  Placeholder.configure({
    placeholder: ({ editor, node, pos }) => {
      if (node.type.name === 'heading') {
        return `Heading ${node.attrs['level']}`;
      }

      if (
        nodeTypesToIgnoreForSlashCommandPlaceholder.includes(node.type.name)
      ) {
        return '';
      }

      // Hide placeholder for paragraphs inside task items
      if (node.type.name === 'paragraph' && pos > 0) {
        const resolvedPos = editor.state.doc.resolve(pos);
        if (resolvedPos.parent.type.name === 'taskItem') {
          return '';
        }
      }

      return "Press '/' for commands";
    },
    includeChildren: true,
  }),
  TextStyle,
  Color,
  Highlight.configure({
    multicolor: true,
  }),
  TaskList.configure({
    HTMLAttributes: {
      class: 'not-prose',
    },
  }),
  TaskItem.configure({
    HTMLAttributes: {
      class: 'flex items-start',
    },
    nested: true,
  }),
];

// Single source of truth for the @tiptap/markdown options. The editor's
// `Markdown.configure` and the compare-time `normalizeMarkdown` MarkdownManager
// MUST use the SAME options, otherwise the canonical form stops matching the
// editor's serialization and the score/diff would read the editor's reformatting
// as a human edit (the phantom-edit class ENG-4453/4659 exists to prevent). Both
// import this constant so they can't drift. `breaks: true` treats a single model
// newline as a hard line break; `gfm` keeps task lists working.
export const markedOptions = { breaks: true, gfm: true };
